import { motion } from "motion/react";
import PropTypes from "prop-types";
import React from "react";

const COMPONENTS_MAP = {
  circle: motion.circle,
  div: motion.div,
  ellipse: motion.ellipse,
  li: motion.li,
  p: motion.p,
  path: motion.path,
  polygon: motion.polygon,
  rect: motion.rect,
  svg: motion.svg,
  ul: motion.ul,
};

/**
 * Transition renders a div, or other DOM element, with its children and animates its as defined by
 * the animate props.
 */
export const Transition = React.forwardRef((props, ref) => {
  const {
    animate,
    children,
    className,
    component = "div",
    componentProps,
    id,
    initial,
    onTransformationEnd,
    role,
    style,
    tabIndex,
    transition,
  } = props;
  const Component = COMPONENTS_MAP[component];

  return <Component {...getProps()}>{children}</Component>;

  function getProps() {
    return {
      animate,
      "aria-hidden": props["aria-hidden"],
      "aria-label": props["aria-label"],
      className,
      id,
      initial: transition?.duration === 0 ? false : initial,
      onAnimationComplete: onTransformationEnd,
      ref,
      role,
      style,
      tabIndex,
      transition,
      ...componentProps,
    };
  }
});

export const transitionPropTypes = {
  /**
   * Values to transition. Can be anything accepted by Motion (https://motion.dev). Will look something like:
   * { opacity: 0.75, x: 200 }.
   */
  animate: PropTypes.object,

  /** Indicates if content is hidden. */
  "aria-hidden": PropTypes.bool,

  /** Indicates if content is hidden. */
  "aria-label": PropTypes.string,

  /** Content that will be rendered. */
  children: PropTypes.node,

  /** Additional classes for positioning the component. */
  className: PropTypes.string,

  /** Additional props to be set. These are CSS values that will not be animated. */
  componentProps: PropTypes.object,

  /** Unique identifier on outermost DOM element. */
  id: PropTypes.string,

  /**
   * Values to initially set for child element. Can be anything accepted by Motion (https://motion.dev).
   * Will look something like: { opacity: 0.75, x: 200 }. Note that animations can be disabled by
   * setting initial to false.
   */
  initial: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),

  /**
   * Function called when the animation is complete. Will not be called if no animation performed
   * because animate values not different from current state.
   */
  onTransformationEnd: PropTypes.func,

  /** Name used for aria role. */
  role: PropTypes.string,

  /** This object provides a way to add CSS values in addition to the properties that will be transformed. */
  style: PropTypes.object,

  /** Optional value to set the tabbing order. This can be negative one, zero, or any positive number. */
  tabIndex: PropTypes.number,

  /**
   * Configuration for how animation will be performed. Can be anything accepted by Motion
   * (https://motion.dev). Will look something like: { duration: 2, ease: "linear" }.
   */
  transition: PropTypes.object,
};
Transition.propTypes = transitionPropTypes;
Transition.displayName = "Transition";
